/**
 * Pattern Suggestion Badge & Panel
 * =================================
 * 
 * UI components for displaying and acting on pattern-based
 * reclassification suggestions.
 * 
 * Features:
 * - Suggestion badges on transactions
 * - Accept/Reject workflow
 * - Pattern confidence indicators
 * - Rationale display for court defensibility
 */

import React, { useState } from 'react';
import { Check, X, Lightbulb, ChevronDown, ChevronUp, AlertTriangle, Info } from 'lucide-react';
import api from '../../utils/api';

interface PatternSuggestion {
  suggestion_id: string;
  pattern_id: string;
  transaction_id: string;
  report_id: string;
  match_score: number;
  matched_keywords: string[];
  current_category: string;
  current_subcategory?: string;
  suggested_category: string;
  suggested_subcategory: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  rationale: string;
}

interface PatternSuggestionBadgeProps {
  suggestion: PatternSuggestion;
  onAccept: () => void;
  onReject: () => void;
  compact?: boolean;
}

/**
 * Compact badge showing suggestion is available
 */
export function PatternSuggestionBadge({ 
  suggestion, 
  onAccept, 
  onReject,
  compact = true 
}: PatternSuggestionBadgeProps) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const confidenceColors = {
    HIGH: 'bg-green-100 text-green-800 border-green-300',
    MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    LOW: 'bg-orange-100 text-orange-800 border-orange-300'
  };

  const confidenceIcons = {
    HIGH: <Check size={12} className="text-green-600" />,
    MEDIUM: <Info size={12} className="text-yellow-600" />,
    LOW: <AlertTriangle size={12} className="text-orange-600" />
  };

  const handleAccept = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await api.post(`/api/v1/patterns/${suggestion.pattern_id}/accept`, {
        transaction_id: suggestion.transaction_id,
        report_id: suggestion.report_id,
        suggestion_id: suggestion.suggestion_id
      });
      onAccept();
    } catch (error) {
      console.error('Error accepting suggestion:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await api.post(`/api/v1/patterns/${suggestion.pattern_id}/reject`, {
        transaction_id: suggestion.transaction_id,
        report_id: suggestion.report_id,
        suggestion_id: suggestion.suggestion_id
      });
      onReject();
    } catch (error) {
      console.error('Error rejecting suggestion:', error);
    } finally {
      setLoading(false);
    }
  };

  if (compact && !expanded) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${confidenceColors[suggestion.confidence]} hover:opacity-80 transition-opacity`}
        title={`Pattern suggestion: ${suggestion.suggested_category} > ${suggestion.suggested_subcategory}`}
      >
        <Lightbulb size={12} />
        <span className="hidden sm:inline">Suggestion</span>
        {confidenceIcons[suggestion.confidence]}
      </button>
    );
  }

  return (
    <div 
      className={`border rounded-lg p-3 ${confidenceColors[suggestion.confidence]} bg-opacity-50`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Lightbulb size={16} className="text-blue-600" />
          <span className="font-medium text-sm">Pattern Suggestion</span>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${confidenceColors[suggestion.confidence]}`}>
            {suggestion.confidence}
          </span>
        </div>
        {compact && (
          <button 
            onClick={() => setExpanded(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <ChevronUp size={16} />
          </button>
        )}
      </div>

      {/* Suggestion Content */}
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-gray-600">Current:</span>
          <span className="font-medium">{suggestion.current_category}</span>
          {suggestion.current_subcategory && (
            <span className="text-gray-500">&gt; {suggestion.current_subcategory}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-600">Suggested:</span>
          <span className="font-medium text-blue-700">{suggestion.suggested_category}</span>
          <span className="text-blue-600">&gt; {suggestion.suggested_subcategory}</span>
        </div>
        
        {/* Rationale */}
        <div className="bg-white/50 rounded p-2 text-xs text-gray-700 italic">
          "{suggestion.rationale}"
        </div>

        {/* Matched Keywords */}
        {suggestion.matched_keywords.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-xs text-gray-500">Matched:</span>
            {suggestion.matched_keywords.map((kw, i) => (
              <span key={i} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                {kw}
              </span>
            ))}
          </div>
        )}

        {/* Match Score */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Match score:</span>
          <div className="flex-1 h-1.5 bg-gray-200 rounded-full max-w-[100px]">
            <div 
              className="h-full bg-blue-500 rounded-full"
              style={{ width: `${suggestion.match_score * 100}%` }}
            />
          </div>
          <span className="text-xs font-medium">{(suggestion.match_score * 100).toFixed(0)}%</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-3 pt-2 border-t border-gray-200">
        <button
          onClick={handleAccept}
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
        >
          <Check size={14} />
          Accept
        </button>
        <button
          onClick={handleReject}
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 disabled:opacity-50 transition-colors"
        >
          <X size={14} />
          Reject
        </button>
      </div>

      {/* Pattern ID for reference */}
      <div className="text-xs text-gray-400 mt-2 text-center">
        Pattern: {suggestion.pattern_id}
      </div>
    </div>
  );
}


interface VarianceAlertProps {
  alert: {
    alert_id: string;
    alert_level: 'critical' | 'warning' | 'info';
    message: string;
    variance: number;
    variance_pct: number;
    line_item: string;
    acknowledged: boolean;
  };
  onAcknowledge: () => void;
}

/**
 * Variance alert badge for P&L cells
 */
export function VarianceAlertBadge({ alert, onAcknowledge }: VarianceAlertProps) {
  const [loading, setLoading] = useState(false);

  const alertStyles = {
    critical: 'bg-red-100 text-red-800 border-red-300',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    info: 'bg-blue-100 text-blue-800 border-blue-300'
  };

  const alertIcons = {
    critical: <AlertTriangle size={14} className="text-red-600" />,
    warning: <AlertTriangle size={14} className="text-yellow-600" />,
    info: <Info size={14} className="text-blue-600" />
  };

  const handleAcknowledge = async () => {
    setLoading(true);
    try {
      await api.post(`/api/v1/alerts/${alert.alert_id}/acknowledge`);
      onAcknowledge();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    } finally {
      setLoading(false);
    }
  };

  if (alert.acknowledged) return null;

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${alertStyles[alert.alert_level]}`}>
      {alertIcons[alert.alert_level]}
      <div className="flex-1">
        <div className="text-sm font-medium">{alert.message}</div>
        <div className="text-xs opacity-75">
          Variance: ${Math.abs(alert.variance).toLocaleString()} ({(alert.variance_pct * 100).toFixed(1)}%)
        </div>
      </div>
      <button
        onClick={handleAcknowledge}
        disabled={loading}
        className="px-2 py-1 text-xs bg-white/50 rounded hover:bg-white/80 transition-colors"
      >
        {loading ? '...' : 'Dismiss'}
      </button>
    </div>
  );
}


interface SuggestionsSummaryProps {
  transactionCount: number;
  suggestionCount: number;
  byConfidence: {
    HIGH: number;
    MEDIUM: number;
    LOW: number;
  };
  onViewAll: () => void;
}

/**
 * Summary panel showing suggestion counts
 */
export function SuggestionsSummary({ 
  transactionCount, 
  suggestionCount, 
  byConfidence,
  onViewAll 
}: SuggestionsSummaryProps) {
  if (suggestionCount === 0) return null;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightbulb size={18} className="text-blue-600" />
          <span className="font-medium text-blue-900">
            {suggestionCount} Pattern Suggestions
          </span>
          <span className="text-sm text-blue-700">
            for {transactionCount} transactions
          </span>
        </div>
        <button
          onClick={onViewAll}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          View All →
        </button>
      </div>
      <div className="flex gap-4 mt-2 text-sm">
        {byConfidence.HIGH > 0 && (
          <span className="text-green-700">
            <Check size={12} className="inline mr-1" />
            {byConfidence.HIGH} HIGH
          </span>
        )}
        {byConfidence.MEDIUM > 0 && (
          <span className="text-yellow-700">
            <Info size={12} className="inline mr-1" />
            {byConfidence.MEDIUM} MEDIUM
          </span>
        )}
        {byConfidence.LOW > 0 && (
          <span className="text-orange-700">
            <AlertTriangle size={12} className="inline mr-1" />
            {byConfidence.LOW} LOW
          </span>
        )}
      </div>
    </div>
  );
}


export default PatternSuggestionBadge;
